import { io } from 'socket.io-client';

let socket = null;
let audioContext = null;
let swRegistration = null;

// Register Service Worker for desktop push notification handling
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js')
    .then((reg) => {
      swRegistration = reg;
      console.log('✅ Notification Service Worker registered:', reg.scope);
    })
    .catch((err) => {
      console.warn('Service Worker registration skipped:', err.message);
    });
}

export function initNotificationService(onNewNotification) {
  if (typeof window === 'undefined') return;

  if (!socket) {
    const socketUrl = process.env.REACT_APP_SOCKET_URL ||
      (window.location.port === '3000' ? 'http://localhost:5000' : null);
    if (!socketUrl) return null;

    socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 10,
    });

    socket.on('notification:new', (notification) => {
      console.log('📢 Real-time notification received:', notification);

      // Display Native Browser Desktop Notification & chime
      showDesktopNotification(notification);

      // Callback to React app state/store
      if (onNewNotification) {
        onNewNotification(notification);
      }
    });
  }

  return socket;
}

export function checkNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted', 'denied', or 'default'
}

export async function requestNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (err) {
    console.error('Error requesting notification permission:', err);
    return Notification.permission;
  }
}

export async function showDesktopNotification(data) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;

  // Auto request permission if status is default
  let currentPermission = Notification.permission;
  if (currentPermission === 'default') {
    currentPermission = await requestNotificationPermission();
  }

  if (currentPermission !== 'granted') return;

  try {
    const title = data.title || 'AI Trading Alert';
    const notifId = data.id || data.metadata?.id;
    
    // Play audio chime for real-time notifications
    playChime(data.priority || 'HIGH');

    // Determine detail redirect URL
    let targetUrl = '/notifications';
    if (notifId) {
      targetUrl = `/notifications?id=${notifId}`;
    } else if (data.symbol || data.orderId) {
      targetUrl = `/equity-orders?symbol=${data.symbol || ''}`;
    }

    // Unique tag timestamp to prevent browser from silently collapsing duplicate-tagged notifications
    const uniqueTag = `${data.eventKey || (notifId ? `notif_${notifId}` : 'notif')}_${Date.now()}`;

    const options = {
      body: data.message || 'New notification update',
      icon: '/favicon.ico',
      tag: uniqueTag,
      requireInteraction: data.priority === 'CRITICAL' || data.priority === 'HIGH',
      data: {
        id: notifId,
        symbol: data.symbol,
        orderId: data.orderId,
        type: data.type,
        url: targetUrl,
      },
    };

    // Standard Window Notification API (instant toast pop-up in active window)
    try {
      const notification = new Notification(title, options);
      notification.onclick = function (event) {
        event.preventDefault();
        try { window.focus(); } catch (e) {}

        if (window.location.pathname + window.location.search !== targetUrl) {
          window.location.href = targetUrl;
        }
        notification.close();
      };
      return;
    } catch (winErr) {
      console.warn('Window Notification API fallback to ServiceWorker:', winErr);
    }

    // Fallback to ServiceWorker notification
    if (swRegistration && 'showNotification' in swRegistration) {
      await swRegistration.showNotification(title, options);
    }
  } catch (err) {
    console.error('Failed to display browser desktop notification:', err);
  }
}

export function testBrowserDesktopNotification(data = {}) {
  const sampleData = {
    id: data.id || Date.now(),
    title: data.title || '🟢 REAL-TIME DESKTOP NOTIFICATION ACTIVE',
    message: data.message || 'This is a native browser desktop notification. Click me to open full notification details!',
    priority: 'HIGH',
    symbol: data.symbol || 'RELIANCE',
    eventKey: `test_notif_${Date.now()}`,
  };

  showDesktopNotification(sampleData);
}

function playChime(priority = 'MEDIUM') {
  try {
    if (!audioContext) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) audioContext = new AudioContextClass();
    }
    if (!audioContext) return;
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();

    osc.type = priority === 'CRITICAL' ? 'sawtooth' : 'sine';
    const freq = priority === 'CRITICAL' ? 880 : priority === 'HIGH' ? 660 : 520;
    osc.frequency.setValueAtTime(freq, audioContext.currentTime);

    gain.gain.setValueAtTime(0.15, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.3);

    osc.connect(gain);
    gain.connect(audioContext.destination);

    osc.start();
    osc.stop(audioContext.currentTime + 0.3);
  } catch (err) {
    // Audio context may be blocked by autoplay policy
  }
}
